import classNames from 'classnames';
import {observer} from 'mobx-react';
import React from 'react';

import {useI18n} from '@udemy/i18n';
import {MarketplaceOnly} from '@udemy/react-brand-components';
import {ButtonProps} from '@udemy/react-core-components';
import {noop} from '@udemy/shared-utils';
import {CouponStore, Discount, MODES, ModesKeys} from '@udemy/shopping';

import {AvailableCoupon} from '../available-coupon/available-coupon.react-component';
import {CartOptIn} from '../cart-opt-in/cart-opt-in.react-component';
import {CouponForm} from '../coupon-form/coupon-form.react-component';
import {EnableCouponInputModeButton} from '../enable-coupon-input-mode-button/enable-coupon-input-mode-button.react-component';
import {ValidDiscount} from '../valid-discount/valid-discount.react-component';
import styles from './redeem-coupon.module.less';

interface KeyEvent {
    type: string;
    key: string;
}

// Move to available coupon when typescript conversion happens
export interface StackableCouponProps {
    code: string;
    rate: string;
    savings_type: string;
    savings_value: string;
}

export interface RedeemCouponProps {
    className?: string;
    couponStore?: CouponStore;
    enableCouponInputModeButtonProps?: ButtonProps;
    enterInputModeClick?: () => void;
    showCartOptIn?: boolean;
    showCouponInputModeButton?: boolean;
    showTitle?: boolean;
    titleLabel?: string;
    removeCouponCodeClick: (code: string) => () => void;
    inputOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    inputOnSubmit: () => void;
    inputIsDisabled?: boolean;
    currentMode: (typeof MODES)[ModesKeys];
    currentCodes: string[];
    currentDiscounts?: Discount[];
    showStackableCoupons?: boolean;
    stackableCoupons?: StackableCouponProps[];
    onApplyStackableCoupon?:
        | ((
              coupon: StackableCouponProps[],
          ) => (e: React.KeyboardEvent<HTMLButtonElement>) => (e: KeyEvent) => void)
        | ((coupon: StackableCouponProps[]) => void);
    submittedCode: string;
    inputCode: string;
    error?: string;
    isInCheckoutPane?: boolean;
}

export const RedeemCoupon: React.FC<RedeemCouponProps> = observer(
    ({
        className,
        couponStore,
        enableCouponInputModeButtonProps = {},
        enterInputModeClick = noop,
        showCartOptIn = false,
        showCouponInputModeButton = false,
        showTitle = true,
        titleLabel,
        removeCouponCodeClick,
        inputOnChange,
        inputOnSubmit,
        inputIsDisabled = false,
        currentMode,
        currentCodes,
        currentDiscounts,
        showStackableCoupons = false,
        stackableCoupons = [],
        onApplyStackableCoupon = noop,
        submittedCode,
        inputCode,
        error,
        isInCheckoutPane = false,
    }) => {
        const {gettext} = useI18n();
        titleLabel = titleLabel ?? gettext('Promotions');

        return (
            <MarketplaceOnly>
                <div className={className}>
                    <div className={styles['add-coupon-button']}>
                        {showCouponInputModeButton && (
                            <EnableCouponInputModeButton
                                onClick={enterInputModeClick}
                                buttonProps={enableCouponInputModeButtonProps}
                            />
                        )}
                    </div>
                    {showTitle && (
                        <p className={classNames('ud-heading-md', styles.title)}>{titleLabel}</p>
                    )}
                    {currentDiscounts && currentDiscounts.length > 0 && (
                        <div className={styles.codes}>
                            {currentDiscounts.reverse().map((d: Discount, index: number) => (
                                <ValidDiscount
                                    key={index}
                                    removeCoupon={removeCouponCodeClick(d.code)}
                                    attemptDiscount={d}
                                    isInCheckoutPane={isInCheckoutPane}
                                />
                            ))}
                        </div>
                    )}
                    {showCartOptIn && couponStore && <CartOptIn couponStore={couponStore} />}
                    {showStackableCoupons && stackableCoupons.length > 0 && (
                        <AvailableCoupon
                            coupons={stackableCoupons}
                            currentCodes={currentCodes}
                            onApplyCoupon={onApplyStackableCoupon}
                        />
                    )}
                    {!showCouponInputModeButton &&
                        [MODES.INPUT, MODES.PENDING].includes(currentMode) && (
                            <CouponForm
                                onChange={inputOnChange}
                                onSubmit={inputOnSubmit}
                                disabled={inputIsDisabled}
                                error={error}
                                submittedCode={submittedCode}
                                inputCode={inputCode}
                                currentMode={currentMode}
                                isInCheckoutPane={isInCheckoutPane}
                            />
                        )}
                </div>
            </MarketplaceOnly>
        );
    },
);
